<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <div class="d-sm-flex align-items-center" style="width:100%;">
                <Form ref="etatForm" :validation-schema="etatSchema" style="width:100%;">
                    <div class="row">
                        <div class="col-4">
                            <div class="form-group mb-15 mt-30 mb-sm-20 mb-md-25">
                                <Field name="recherche" type="text" v-model="recherche" style="margin-top:30px;"
                                    class="form-control shadow-none fs-md-15 text-black" placeholder="Rechercher" />
                                <ErrorMessage name="recherche" class="text-danger" />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                <label class="d-block text-black fw-semibold mb-10">
                                    Date Début
                                </label>
                                <Field name="debut" type="date" v-model="debut" style="background-color: #fff;"
                                    class="form-control shadow-none fs-md-15 text-black"
                                    placeholder="Choisisser la date" />
                                <ErrorMessage name="debut" class="text-danger" />
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                <label class="d-block text-black fw-semibold mb-10">
                                    Date Fin
                                </label>
                                <Field name="fin" v-model="fin" type="date" style="background-color: #fff;"
                                    class="form-control shadow-none fs-md-15 text-black"
                                    placeholder="Choisisser la date" />
                                <ErrorMessage name="fin" class="text-danger" />
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
            <div class="table-responsive">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Date.Cmd
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Client
                            </th>
                             <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Produit Vendu
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Prix.U
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                Qté
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(prod, index) in produits" :key="index">
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ prod.commandeClient?.dateCommande ? format_Date(prod.commandeClient.dateCommande) :
                                    'N/A' }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ (prod.commandeClient?.client?.nomClient || '') + ' ' +
                                    (prod.commandeClient?.client?.prenomClient || '') }}
                            </td>
                             <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ prod.libelleProduit || 0 }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ prod.prixVente || 0 }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                                {{ prod.qtite || 0 }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
                <PaginationComponent :page="page" :totalPages="totalPages" :totalElements="totalElements" :limit="limit"
                    @paginate="handlePaginate" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, Ref, toRef, watch } from "vue";
import Swal from "sweetalert2";
import { Depense } from "@/models/Depense";
import ApiService from "@/services/ApiService";
import { suppression, error, format_Date } from "@/utils/utils";
import PaginationComponent from '@/components/Utilities/Pagination.vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import JwtService from "@/services/JwtService";
import * as Yup from 'yup';

export default defineComponent({
    name: "EtatPointVente",
    components: {
        Form,
        Field,
        ErrorMessage,
        PaginationComponent
    },
    setup() {
        onMounted(() => {
            getAllVentes(1, 10, recherche.value, debut.value, fin.value);
        });

        const etatForm = ref<any | null>({});
        const etatSchema = Yup.object().shape({
            recherche: Yup.string().notRequired(),
            debut: Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .notRequired(),
            fin: Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === "" ? null : value))
                .notRequired()
                .min(
                    Yup.ref('debut'),
                    "La date de fin doit être supérieure ou égale à la date de début"
                ),
        });
        const debut = ref("");
        const fin = ref("");
        const recherche: Ref<string> = ref("");

        watch(
            () => [recherche.value, debut.value, fin.value],
            (newValues) => {
                if (debut.value && fin.value && new Date(fin.value) < new Date(debut.value)) {
                    return;
                }
                getAllVentes(1, 10, newValues[0], newValues[1], newValues[2]);
            }
        )
        const produits = ref<Array<any>>([]);

        const searchTerm = ref('');
        const page = ref(1);
        const totalPages = ref(0);
        const limit = ref(10);
        const totalElements = ref(0);

        const handlePaginate = ({ page_, limit_ }) => {
            try {
                page.value = page_;
                getAllVentes(page_, limit_, recherche.value, debut.value, fin.value);
            } catch (error) {
                //
            }
        };

        function rechercher() {
            getAllVentes(page.value, limit.value, searchTerm.value);
        }

        function getAllVentes(page = 1, limi = 10, searchTerm = '', debut = '', fin = '') {
            ApiService.get(`/etatProduitVenduPeriodique?start=${page}&limit=${limi}&motCle=${searchTerm}&debut=${debut}&fin=${fin}&`)

                .then(({ data }) => {
                    console.log("data ====> ", data);
                    produits.value = data.data.retour.entities;

                    totalPages.value = data.data.totalPages;
                    limit.value = data.data.limit;
                    totalElements.value = data.data.totalElements;
                    return data.data;
                })
                .catch(({ response }) => {
                    error(response.data.message)
                });
        }

        const getStatutLiv = (prod: any) => {
            const livraisons = prod.commandeClient?.commandeClientLivres;
            return livraisons?.[livraisons.length - 1]?.statutLiv || 'N/A';
        };


        const privileges = ref<Array<string>>(JwtService.getPrivilege());

        const checkPermission = (name) => {
            return privileges.value.includes(name);
        }
        return {
            produits,
            checkPermission,
            getAllVentes,
            page,
            format_Date,
            totalPages,
            limit,
            debut, fin, etatSchema,
            totalElements,
            handlePaginate,
            rechercher,
            recherche,
            searchTerm,
            etatForm,
            getStatutLiv
        };
    },
});
</script>